import { Observable, of } from 'rxjs';
import { Location } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { SlActions } from '../ActionsService/sl-actions';
import {
  ActionType,
  CurrentPageType,
  LessonQuitPromptClickTypes,
  ModalKeys,
  RequestType,
  TrackActionName,
  UserSettingKey,
  OnboardingState,
  IRouteData,
} from '../../global-interface';
import {
  getLockedCardModalMessages,
  SlLEContext,
} from '../../global-constants';
import { SlDataService } from '../sl-data.service';
import { SlNavigationService } from '../sl-navigation.service';
import { SlCommonService } from './sl-common.service';
import { SlLessonService } from './sl-lesson.service';
import { SlLeaderboardService } from './sl-leaderboard.service';
import { DeviceInfo, detectDevice } from '../../../../shared/public/SlUtils/detectDevice';
import { SlStorageKeys } from '../../../../shared/public/globalInterfaces/globalInterfaces';
import { deleteSearchParam } from '../../../../shared/public/SlUtils/deleteSearchParam';
import {
  IMaterialInfo, LocationType, IMaterialSolutionDTO, MaterialType, VisibilityStatus, IMaterial,
  MaterialRequirementType,
} from '../../../../api/private/learn-engine-api/learn-engine-api.interface';
import {
  Service, Container, StorageService, AuthService, HeartService, BitService, BitLocationType, HeartModalSource, LearningExperienceType, OwnershipRequirementType, ILearningExperience, history,
} from '../../../../symphony';
import { getLEStatus } from '../../utils/helpers';
import { DynamicPaywallLoader } from '../../../../_paywalls/public/DynamicPaywallLoader';
import { Touchpoint } from '../../../../api/public/subscription-api';

@Service()
export class SlEventsService {
  private actions$ = Container.take(SlLEContext, SlActions).actions$;

  private data = Container.take(SlLEContext, SlDataService);

  private storageService = Container.take('global', StorageService);

  private navigationService = Container.take(
    SlLEContext,
    SlNavigationService,
  );

  private authService = Container.take('global', AuthService);

  private heartService = Container.take('global', HeartService);

  private bitService = Container.take('global', BitService);

  private commonService = Container.take(SlLEContext, SlCommonService);

  private lessonService = Container.take(SlLEContext, SlLessonService);

  private paywallLoader = DynamicPaywallLoader.ensureRegistered();

  private leaderboardService = Container.take(
    SlLEContext,
    SlLeaderboardService,
  );

  private deviceInfo: DeviceInfo = detectDevice();

  public onCoursesMount = (): Observable<null> => {
    this.actions$.next({
      type: ActionType.GetCourseCategories,
      requestType: RequestType.Parallel,
    });

    if (!this.authService.isLoggedIn()) {
      this.actions$.next({
        type: ActionType.GetProgrammingLanguages,
        requestType: RequestType.Parallel,
      });
    }

    this.actions$.next({
      type: ActionType.SetBitLocation,
      requestType: RequestType.Parallel,
      data: {
        bitLocationType: BitLocationType.CourseList,
      },
    });

    this.actions$.next({
      type: ActionType.Track,
      requestType: RequestType.Parallel,
      data: {
        trackData: {
          actionName: TrackActionName.OnCoursesMount,
        },
      },
    });
    return of(null);
  };

  public onCourseContainerMount = (
    alias: string,
    location: Location,
  ): Observable<null> => {
    this.data.routeData$.next({
      courseAlias: alias,
      currentPage: CurrentPageType.CoursePage,
    });
    const session: string = this.storageService.load(
      SlStorageKeys.Sl_LE_SESSION,
      'session',
    );
    if (session && this.data.onboarding$.value === null) {
      this.actions$.next({
        type: ActionType.RemoveLessonSessionDataRecording,
        requestType: RequestType.Parallel,
      });
    }
    const params = new URLSearchParams(location.search.toLocaleLowerCase());

    if (params.get('openheartmodal')) {
      deleteSearchParam('openheartmodal', location);
      this.actions$.next({
        type: ActionType.OpenHeartModal,
        requestType: RequestType.Parallel,
        data: {
          heartModalSource: HeartModalSource.LessonOpenFail,
        },
      });
    }

    if (this.data.course$.value && !this.data.currentItems$.value.length) {
      // TODO: check
      this.actions$.next({
        type: ActionType.SetCourseCurrentItems,
        requestType: RequestType.Parallel,
        data: {
          material: this.data.course$.value,
        },
      });
    }

    return of(null);
  };

  public onLearningPathContainerMount = (
    alias: string,
    location: Location,
  ): Observable<null> => {
    this.data.routeData$.next({
      pathAlias: alias,
      currentPage: CurrentPageType.PathPage,
    });
    const session: string = this.storageService.load(
      SlStorageKeys.Sl_LE_SESSION,
      'session',
    );
    if (session && this.data.onboarding$.value === null) {
      this.actions$.next({
        type: ActionType.RemoveLessonSessionDataRecording,
        requestType: RequestType.Parallel,
      });
    }
    const params = new URLSearchParams(location.search.toLocaleLowerCase());

    if (params.get('openheartmodal')) {
      deleteSearchParam('openheartmodal', location);
      this.actions$.next({
        type: ActionType.OpenHeartModal,
        requestType: RequestType.Parallel,
        data: {
          heartModalSource: HeartModalSource.LessonOpenFail,
        },
      });
    }

    if (
      this.data.learningPath$.value
      && !this.data.currentItems$.value.length
    ) {
      // TODO: check
      this.actions$.next({
        type: ActionType.SetPathCurrentItems,
        requestType: RequestType.Parallel,
        data: {
          path: this.data.learningPath$.value,
          alias:
            this.data.course$.value?.materialInfo?.header?.alias
            || null,
        },
      });
    }

    return of(null);
  };

  public onCurrentModuleChildrenMount = (id: number): Observable<null> => {
    this.actions$.next({
      type: ActionType.ScrollIntoCurrentMaterialElement,
      requestType: RequestType.Parallel,
      data: {
        id,
      },
    });
    return of(null);
  };

  public onSkipAheadJumpClick = (dynamicId: number): Observable<null> => {
    if (this.heartService.isRanOutOfHearts()) {
      this.actions$.next({
        type: ActionType.OpenHeartModal,
        requestType: RequestType.Parallel,
        data: {
          heartModalSource: HeartModalSource.LessonOpenFail
        },
      });
    } else {
      this.navigationService.navigation$.next({
        materialRelationId: dynamicId,
        courseAlias: this.data.course$.value.materialInfo.header.alias,
        isSkipAhead: true,
      });
    }

    return of(null);
  };

  public onLessonContainerMount = (
    type: LearningExperienceType,
    alias: string,
    materialId: number,
    lessonPageIndex,
    isSkipAhead: boolean,
  ): Observable<null> => {
    this.data.isSkipAhead.next(isSkipAhead);

    if (!this.authService.isLoggedIn()) {
      this.actions$.next({
        type: ActionType.NavigateToUserLogin,
        requestType: RequestType.Parallel,
      });
      return of(null);
    }

    if (this.deviceInfo.isMobile) {
      this.data.deviceInfo$.next(this.deviceInfo);
      this.navigationService.navigation$.next({ toLEPage: true });
      return of(null);
    }

    if (this.data.onboarding$.value === null) {
      this.actions$.next({
        type: ActionType.RemoveLessonSessionDataRecording,
        requestType: RequestType.Parallel,
      });
    } else {
      this.data.onboarding$.next(null);
    }

    const routeData: IRouteData = {
      materialId,
      lessonPageIndex,
      currentPage: CurrentPageType.LessonPage,
    };

    if (window.location.pathname.split('/')[7] === 'complete') {
      routeData.currentPage = CurrentPageType.LessonCompletedPage;
    }

    if (type === LearningExperienceType.Course) {
      routeData.courseAlias = alias;
    } else if (type === LearningExperienceType.LearningPath) {
      routeData.pathAlias = alias;
    }
    this.data.routeData$.next(routeData);

    this.actions$.next({
      type: ActionType.SetLessonData,
      requestType: RequestType.Cancelable,
      data: {
        id: materialId,
        lessonPageIndex,
      },
    });

    this.actions$.next({
      type: ActionType.SetBitLocation,
      requestType: RequestType.Parallel,
      data: {
        bitLocationType: BitLocationType.Lesson,
        materialId,
      },
    });

    return of(null);
  };

  public onLessonPageMount = (): Observable<null> => {
    this.actions$.next({
      type: ActionType.InitializeLessonPage,
      requestType: RequestType.Cancelable,
    });

    this.actions$.next({
      type: ActionType.TrySolveMaterial,
      requestType: RequestType.Parallel,
    });
    const lessonPageIndex = this.data.lessonPageIndex$.value - 1;

    this.actions$.next({
      type: ActionType.Track,
      requestType: RequestType.Parallel,
      data: {
        trackData: {
          actionName: TrackActionName.OnMaterialMount,
          materialInfo:
            this.data.lesson$.value.children[lessonPageIndex]
              .materialInfo,
        },
      },
    });

    return of(null);
  };

  public onTheoryMount = (materialInfo: IMaterialInfo): Observable<null> => {
    const mutatedData = [...this.data.solutions$.value];
    mutatedData.push(materialInfo);
    this.data.solutions$.next(mutatedData);
    return of(null);
  };

  // TODO: send correct data shape to solveMaterial
  public onTIYWidgetMount = (): Observable<null> => of(null);

  public onRouteWithLEContainerMount = (
    type: LearningExperienceType,
    alias: string,
    locationUrl: LocationType,
  ): Observable<null> => {
    this.data.routeData$.next({
      ...this.data.routeData$.value,
      locationUrl: locationUrl || LocationType.CourseList,
    });

    this.data.alias$.next(alias);

    if (
      locationUrl === LocationType.Onboarding
      && this.authService.isLoggedIn()
    ) {
      this.actions$.next({
        type: ActionType.StartOnboarding,
        requestType: RequestType.Parallel,
      });
    }

    this.actions$.next({
      type:
        type === LearningExperienceType.LearningPath
          ? ActionType.GetLearningPath
          : ActionType.GetCourse,
      requestType: RequestType.Parallel,
      data: {
        alias,
      },
    });

    return of(null);
  };

  public onTIYMaterialMount = (
    courseAlias: string,
    materialId: number,
  ): Observable<null> => {
    if (this.deviceInfo.isMobile) {
      this.data.deviceInfo$.next(this.deviceInfo);
      this.navigationService.navigation$.next({
        toLEPage: true,
      });

      return of(null);
    }
    this.data.routeData$.next({
      courseAlias,
      materialId,
      currentPage: CurrentPageType.TIY,
    });
    return of(null);
  };

  public onCCMaterialMount = (
    courseAlias: string,
    materialId: number,
  ): Observable<null> => {
    if (this.deviceInfo.isMobile) {
      this.data.deviceInfo$.next(this.deviceInfo);
      this.navigationService.navigation$.next({
        toLEPage: true,
      });

      return of(null);
    }
    this.data.routeData$.next({
      courseAlias,
      materialId,
      currentPage: CurrentPageType.CodeCoach,
    });
    return of(null);
  };

  public onCRMaterialMount = (
    courseAlias: string,
    materialId: number,
  ): Observable<null> => {
    if (this.deviceInfo.isMobile) {
      this.data.deviceInfo$.next(this.deviceInfo);
      this.navigationService.navigation$.next({
        toLEPage: true,
      });

      return of(null);
    }
    this.data.routeData$.next({
      courseAlias,
      materialId,
      currentPage: CurrentPageType.CodeRepo,
    });
    return of(null);
  };

  public onLessonPageIndexChange = (
    id: number,
    lessonPageIndex: number,
  ): Observable<null> => {
    this.actions$.next({
      type: ActionType.SetLessonData,
      requestType: RequestType.Cancelable,
      data: {
        id,
        lessonPageIndex,
      },
    });
    this.data.routeData$.next({
      ...this.data.routeData$.value,
      lessonPageIndex,
    });

    this.data.quizFeedback$.next(null);
    return of(null);
  };

  public onCourseClick = (alias: string): Observable<null> => {
    this.actions$.next({
      type: ActionType.CleanupCourse,
      requestType: RequestType.Parallel,
      data: {
        alias,
      },
    });
    return of(null);
  };

  public onPathClick = (alias: string): Observable<null> => {
    this.actions$.next({
      type: ActionType.CleanupPath,
      requestType: RequestType.Parallel,
      data: {
        alias,
      },
    });
    return of(null);
  };

  public onCourseNotFoundClick = (): Observable<null> => {
    this.navigationService.navigation$.next({
      toMainPage: true,
    });
    return of(null);
  };

  public onLessonCompletedModalContinueClick = (): Observable<null> => {
    this.actions$.next({
      type: ActionType.NextLCSIteration,
      requestType: RequestType.Parallel,
    });
    return of(null);
  };

  public onFirstLessonCelebrationContinueClick = (): Observable<null> => {
    this.actions$.next({
      type: ActionType.UpdateUserSettings,
      requestType: RequestType.Parallel,
      data: {
        userSetting: {
          key: UserSettingKey.IntroBitsAndXpsShown,
          value: 'true',
        },
      },
    });
    this.actions$.next({
      type: ActionType.NextLCSIteration,
      requestType: RequestType.Parallel,
    });
    this.actions$.next({
      type: ActionType.Track,
      requestType: RequestType.Parallel,
      data: {
        trackData: {
          actionName:
            TrackActionName.OnCompleteFirstLessonCelebrationClick,
        },
      },
    });
    return of(null);
  };

  public onStreakCelebrationScreenContinueClick = (): Observable<null> => {
    this.actions$.next({
      type: ActionType.NextLCSIteration,
      requestType: RequestType.Parallel,
    });
    return of(null);
  };

  public onLeaderboardScreenContinueClick = (): Observable<null> => {
    this.actions$.next({
      type: ActionType.NextLCSIteration,
      requestType: RequestType.Parallel,
    });
    this.actions$.next({
      type: ActionType.Track,
      requestType: RequestType.Parallel,
      data: {
        trackData: {
          actionName:
            TrackActionName.OnLeaderboardScreenContinueClick,
        },
      },
    });
    return of(null);
  };

  public onLeaderboardScreenMount = (): Observable<null> => {
    this.actions$.next({
      type: ActionType.Track,
      requestType: RequestType.Parallel,
      data: {
        trackData: {
          actionName: TrackActionName.OnLeaderboardScreenImpression,
        },
      },
    });
    return of(null);
  };

  public onUnlockQuizAnswerMount = (): Observable<null> => {
    this.bitService.trackUnlockQuizImpression();
    return of(null);
  };

  public onUnlockQuizAnswerButtonClick = (): Observable<IMaterialSolutionDTO> => {
    this.bitService.trackUnlockQuizButtonClick();
    return this.lessonService.unlockQuizAnswer();
  };

  public onUnlockQuizAnswerClose = (): Observable<null> => {
    this.bitService.trackUnlockQuizClose();
    return of(null);
  };

  public onLessonNotFoundClick = (): Observable<null> => {
    const { routeData$ } = this.data;

    if (!routeData$.value?.courseAlias && !routeData$.value?.pathAlias) {
      this.navigationService.navigation$.next({
        toMainPage: true,
      });
    } else {
      this.navigationService.navigation$.next({
        toLEPage: true,
      });
    }
    return of(null);
  };

  public onModuleClick = (relationId: number): Observable<null> => {
    this.actions$.next({
      type: ActionType.Track,
      requestType: RequestType.Parallel,
      data: {
        trackData: {
          actionName: TrackActionName.OnMaterialClick,
          relationId,
        },
      },
    });
    return of(null);
  };

  private handleLockedCardClick = (typeId: MaterialType): void => {
    this.actions$.next({
      type: ActionType.OnLockedCardClick,
      requestType: RequestType.Parallel,
      data: {
        typeId,
      },
    });
  };

  public onLessonClick = (
    materialInfo: IMaterialInfo
  ): Observable<null> => {
    const {
      header: { typeId, materialRelationId: id },
      status: { visibility, ownership },
      requirementTypeId
    } = materialInfo;

    this.actions$.next({
      type: ActionType.Track,
      requestType: RequestType.Parallel,
      data: {
        trackData: {
          actionName: TrackActionName.OnMaterialClick,
          relationId: id,
        },
      },
    });

    if (visibility === VisibilityStatus.Locked) {
      this.handleLockedCardClick(typeId);
    } else if (this.deviceInfo.isMobile) {
      this.data.deviceInfo$.next(this.deviceInfo);
    } else if (
      !ownership.isOwned
    ) {
      const isRequired = requirementTypeId !== MaterialRequirementType.Optional;
      const touchpoint = isRequired
        ? Touchpoint.blockedLearning
        : ({
          [MaterialType.Booster]: Touchpoint.booster,
          [MaterialType.Lesson]: Touchpoint.lesson,
          [MaterialType.ModuleQuiz]: Touchpoint.moduleQuiz
        })[typeId];
      this.paywallLoader.openPaywall({
        touchpoint, props: {}
      });
    } else if (
      this.heartService.isRanOutOfHearts()
    ) {
      this.actions$.next({
        type: ActionType.OpenHeartModal,
        requestType: RequestType.Parallel,
        data: {
          heartModalSource: HeartModalSource.LessonOpenFail,
          callback: () => this.lessonService.openLesson(id),
        },
      });
    } else {
      this.actions$.next({
        type: ActionType.OpenLesson,
        requestType: RequestType.Parallel,
        data: {
          id,
        },
      });
    }

    return of(null);
  };

  public onCloseLessonClick = (): Observable<null> => {
    const { lessonQuitPromptInfo$ } = this.data;

    if (lessonQuitPromptInfo$.value?.shouldShow) {
      this.actions$.next({
        type: ActionType.OpenModal,
        requestType: RequestType.Parallel,
        data: {
          modalId: ModalKeys.LessonQuitPrompt,
        },
      });

      this.actions$.next({
        type: ActionType.Track,
        requestType: RequestType.Parallel,
        data: {
          trackData: {
            actionName: TrackActionName.LessonQuitPromptImpression,
          },
        },
      });
    } else {
      this.actions$.next({
        type: ActionType.CloseLesson,
        requestType: RequestType.Parallel,
      });
    }

    return of(null);
  };

  public onLessonQuitPromptContinueClick = (): Observable<null> => {
    this.actions$.next({
      type: ActionType.CloseModal,
      requestType: RequestType.Parallel,
      data: {
        modalId: ModalKeys.LessonQuitPrompt,
      },
    });

    this.actions$.next({
      type: ActionType.Track,
      requestType: RequestType.Parallel,
      data: {
        trackData: {
          actionName: TrackActionName.LessonQuitPromptClick,
          quitLessonClickType: LessonQuitPromptClickTypes.Stay,
        },
      },
    });

    return of(null);
  };

  private getNextMaterial = (): IMaterial => {
    const { lesson$, course$ } = this.data;

    const parentRelationId = lesson$.value?.materialInfo?.header.parentRelationId;

    const parentData = course$.value.children.find(
      (item) => item.materialInfo.header.materialRelationId === parentRelationId,
    );

    const nextMaterialData = parentData?.children?.[lesson$.value.materialInfo.orderNumber];
    return nextMaterialData;
  };

  public onLessonQuitPromptLeaveClick = (): Observable<null> => {
    this.actions$.next({
      type: ActionType.CloseLesson,
      requestType: RequestType.Parallel,
    });

    this.actions$.next({
      type: ActionType.Track,
      requestType: RequestType.Parallel,
      data: {
        trackData: {
          actionName: TrackActionName.LessonQuitPromptClick,
          quitLessonClickType: LessonQuitPromptClickTypes.Quit,
        },
      },
    });

    return of(null);
  };

  public onLessonQuitPromptUnmount = (): Observable<null> => {
    if (this.data.modals$.value?.[ModalKeys.LessonQuitPrompt]) {
      this.actions$.next({
        type: ActionType.CloseModal,
        requestType: RequestType.Parallel,
        data: {
          modalId: ModalKeys.LessonQuitPrompt,
        },
      });
    }

    return of(null);
  };

  public onLockedCardContinueClick = (): Observable<null> => {
    this.actions$.next({
      type: ActionType.CloseModal,
      requestType: RequestType.Parallel,
      data: {
        modalId: ModalKeys.LockedCard,
      },
    });

    return of(null);
  };

  public onLockedCardClick = (typeId: MaterialType): Observable<null> => {
    if (!typeId) return of(null);

    this.data.lockedCardModalData$.next(
      getLockedCardModalMessages()[typeId],
    );

    this.actions$.next({
      type: ActionType.OpenModal,
      requestType: RequestType.Parallel,
      data: {
        modalId: ModalKeys.LockedCard,
      },
    });

    return of(null);
  };

  public onCPPracticeClick = (
    materialInfo: IMaterialInfo,
    returnUrl: string,
  ): Observable<null> => {
    const {
      header: { typeId, materialRelationId: id },
      status,
      requirementTypeId
    } = materialInfo;

    this.actions$.next({
      type: ActionType.Track,
      requestType: RequestType.Parallel,
      data: {
        trackData: {
          actionName: TrackActionName.OnMaterialClick,
          relationId: id,
        },
      },
    });

    if (status.visibility === VisibilityStatus.Locked) {
      this.handleLockedCardClick(typeId);

      return of(null);
    }

    if (this.deviceInfo.isMobile) {
      this.data.deviceInfo$.next(this.deviceInfo);
      return of(null);
    }

    if (this.heartService.isRanOutOfHearts()) {
      this.actions$.next({
        type: ActionType.OpenHeartModal,
        requestType: RequestType.Parallel,
        data: {
          heartModalSource: HeartModalSource.LessonOpenFail,
          callback: () => {
            this.actions$.next({
              type: ActionType.OpenCodeProject,
              requestType: RequestType.Parallel,
              data: {
                id,
                returnUrl,
              },
            });
          },
        },
      });
    } else if (status.ownership.isOwned) {
      this.actions$.next({
        type: ActionType.OpenCodeProject,
        requestType: RequestType.Parallel,
        data: {
          id,
          returnUrl,
        },
      });
    } else {
      const isRequired = requirementTypeId !== MaterialRequirementType.Optional;
      const touchpoint = isRequired ? Touchpoint.blockedLearning : Touchpoint.codeProject;
      this.paywallLoader.openPaywall({
        touchpoint, props: {}
      });
    }

    return of(null);
  };

  public onCRPracticeClick = (
    materialInfo: IMaterialInfo,
  ): Observable<null> => {
    const {
      header: { typeId, materialRelationId: id },
      status,
      requirementTypeId
    } = materialInfo;

    this.actions$.next({
      type: ActionType.Track,
      requestType: RequestType.Parallel,
      data: {
        trackData: {
          actionName: TrackActionName.OnMaterialClick,
          relationId: id,
        },
      },
    });

    if (status.visibility === VisibilityStatus.Locked) {
      this.handleLockedCardClick(typeId);

      return of(null);
    }

    if (this.deviceInfo.isMobile) {
      this.data.deviceInfo$.next(this.deviceInfo);
      return of(null);
    }

    if (status.ownership.isOwned) {
      this.actions$.next({
        type: ActionType.OpenCodeRepo,
        requestType: RequestType.Parallel,
        data: {
          id,
        },
      });
    } else {
      const isRequired = requirementTypeId !== MaterialRequirementType.Optional;
      const touchpoint = isRequired ? Touchpoint.blockedLearning : Touchpoint.codeRepo;
      this.paywallLoader.openPaywall({
        touchpoint, props: {}
      });
    }

    return of(null);
  };

  public onLPCommentsClick = (): Observable<null> => {
    this.actions$.next({
      type: ActionType.Track,
      requestType: RequestType.Parallel,
      data: {
        trackData: {
          actionName: TrackActionName.CommentClick,
        },
      },
    });
    this.data.isShownLPComments$.next(true);
    return of(null);
  };

  public onCloseLPCommentsClick = (): Observable<null> => {
    this.data.isShownLPComments$.next(false);
    return of(null);
  };

  public onHeartIconClick = (heartModalSource: HeartModalSource): Observable<null> => {
    this.actions$.next({
      type: ActionType.OpenHeartModal,
      requestType: RequestType.Parallel,
      data: {
        heartModalSource,
      },
    });

    if (heartModalSource === HeartModalSource.LessonIconClick) {
      this.heartService.trackIconClick();
    } else {
      this.heartService.trackIconProClick();
    }

    return of(null);
  };

  public certificateCTAClick = (): Observable<null> => {
    this.actions$.next({
      type: ActionType.Track,
      requestType: RequestType.Parallel,
      data: {
        trackData: {
          actionName: TrackActionName.CertificateCTAClick,
        },
      },
    });

    return of(null);
  };

  public onCertificateCourseComplete = (alias: string): Observable<null> => {
    this.actions$.next({
      type: ActionType.GetCertificate,
      requestType: RequestType.Parallel,
      data: {
        alias,
      },
    });

    return of(null);
  };

  public onCodeSnippetClick = (): Observable<null> => {
    this.actions$.next({
      type: ActionType.Track,
      requestType: RequestType.Parallel,
      data: {
        trackData: {
          actionName: TrackActionName.CodeSnippetClick,
        },
      },
    });
    return of(null);
  };

  public onImageClick = (imgUrl: string): Observable<null> => {
    this.actions$.next({
      type: ActionType.Track,
      requestType: RequestType.Parallel,
      data: {
        trackData: {
          actionName: TrackActionName.ImageClick,
          imgUrl,
        },
      },
    });
    return of(null);
  };

  public onCloseNotAllowedFeatureModalClick = (): Observable<null> => {
    this.data.deviceInfo$.next(null);
    return of(null);
  };

  public onLandingPageCTAClick = (): Observable<null> => {
    this.commonService.startLearning();

    this.actions$.next({
      type: ActionType.Track,
      requestType: RequestType.Parallel,
      data: {
        trackData: {
          actionName: TrackActionName.OnLandingPageCTAClick,
        },
      },
    });
    return of(null);
  };

  public onFreeHeartRefillCtaClick = (): Observable<null> => {
    this.actions$.next({
      type: ActionType.RefillFreeHearts,
      requestType: RequestType.Parallel,
    });
    this.actions$.next({
      type: ActionType.TrackFirstRefillClick,
      requestType: RequestType.Parallel,
    });
    return of(null);
  };

  public onUpdateLPCommentsCB = (count: number): Observable<null> => {
    this.data.lpComments$.next({
      ...this.data.lpComments$.value,
      count,
    });
    return of(null);
  };

  public onOnboardingCourseReady = (course: IMaterial, le: ILearningExperience): Observable<null> => {
    const { children: modules } = course;

    if (getLEStatus(le) === 'early-bird') {
      this.data.onboarding$.next(OnboardingState.Ready);
    } else {
      this.actions$.next({
        type: ActionType.SetLessonData,
        requestType: RequestType.Cancelable,
        data: {
          id: modules[0].children[0].materialInfo.header.materialRelationId,
          lessonPageIndex: 0,
        },
      });
    }

    return of(null);
  };

  public onOnboardingLessonReady = (): Observable<null> => {
    this.data.onboarding$.next(OnboardingState.Ready);

    return of(null);
  };

  public onOnboardingLoopIteration = (): Observable<null> => {
    if (this.data.onboarding$.value === OnboardingState.Done) {
      this.data.onboarding$.next(OnboardingState.Completed);
      this.actions$.next({
        type: ActionType.OnOnboardingComplete,
        requestType: RequestType.Parallel,
      });
    }

    return of(null);
  };

  public onLessonPageUnmount = (): Observable<null> => {
    this.data.lpComments$.next(null);
    this.data.disabledActions$.next({
      ...this.data.disabledActions$.value,
      question: null,
    });
    this.data.loading$.next(null);
    return of(null);
  };

  public onLeaderboardScreenUnmount = (): Observable<null> => this.leaderboardService.cleanupLeaderboardData();

  public onSlLearnEngineUnmount = (): Observable<null> => {
    this.actions$.next({
      type: ActionType.StartLessonSessionDataRecording,
      requestType: RequestType.Parallel,
    });
    return of(null);
  };

  public onCourseUnmount = (): Observable<null> => {
    this.data.lastAvailableMaterialIdInCurrentSession$.next(null);
    this.data.lastCompletedMaterialIdInCurrentSession$.next(null);
    return of(null);
  };

  public onLessonContainerUnmount = (): Observable<null> => {
    this.actions$.next({
      type: ActionType.ClearLessonData,
      requestType: RequestType.Cancelable,
    });
    return of(null);
  };

  public onLandingPageMount = (alias: string): Observable<null> => {
    this.actions$.next({
      type: ActionType.GetLandingPageData,
      requestType: RequestType.Parallel,
      data: {
        alias,
      },
    });
    return of(null);
  };

  public onCourseMount = (course: IMaterialInfo, isCourseAvailable: boolean): Observable<null> => {
    if (this.authService.isLoggedIn()) {
      this.actions$.next({
        type: ActionType.SetBitLocation,
        requestType: RequestType.Parallel,
        data: {
          bitLocationType: BitLocationType.LearningPath,
          bitLocationDetails: course.header.alias,
          materialHeader: course.header,
        },
      });
      this.actions$.next({
        type: ActionType.Track,
        requestType: RequestType.Parallel,
        data: {
          trackData: {
            actionName: TrackActionName.OnMaterialMount,
            materialInfo: course,
          },
        },
      });
    }
    this.data.isCourseAvailable$.next(isCourseAvailable);
    return of(null);
  };

  private getAiChatSettings = async () => {
    try {
      this.data.loading$.next({
        ...this.data.loading$.value,
        isAIChatSettingsLoading: true,
      });
      const response = await fetch('https://blob.sololearn.com/assets/ai-chat-settings.json');
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      Container.register('global', 'ai-chat-settings', data);
      this.data.loading$.next({
        ...this.data.loading$.value,
        isAIChatSettingsLoading: false,
      });
    } catch (error) {
      console.error('Could not fetch the data:', error);
    }
  };

  public onLessonMount = (): Observable<null> => {
    const { lesson$ } = this.data;
    if (lesson$.value) {
      this.actions$.next({
        type: ActionType.Track,
        requestType: RequestType.Parallel,
        data: {
          trackData: {
            actionName: TrackActionName.OnMaterialMount,
            materialInfo: lesson$.value.materialInfo,
          },
        },
      });
    }
    if (
      this.authService.isLoggedIn()
      && lesson$.value?.materialInfo.status?.isCompleted === false
    ) {
      this.actions$.next({
        type: ActionType.GetLeaderboardPredictedState,
        requestType: RequestType.Parallel,
      });
    }
    this.paywallLoader.prefetchPaywall({
      touchpoint: Touchpoint.lessonComplete,
      props: {}
    });
    this.data.lessonSessionGuId$.next(uuidv4());
    this.getAiChatSettings();

    return of(null);
  };

  public onSlLearnEngineMount = (): Observable<null> => {
    if (this.authService.isLoggedIn()) {
      this.actions$.next({
        type: ActionType.GetHeartConfigs,
        requestType: RequestType.Parallel,
      });
      this.actions$.next({
        type: ActionType.GetLessonCompleteScreens,
        requestType: RequestType.Parallel,
      });
      this.actions$.next({
        type: ActionType.GetUserSettings,
        requestType: RequestType.Parallel,
      });
    }
    return of(null);
  };

  public onStreakCelebrationMount = (): Observable<null> => {
    this.actions$.next({
      type: ActionType.TrackStreakCelebrationImpression,
      requestType: RequestType.Parallel,
    });

    return of(null);
  };

  public onFirstLessonCompleteCelebrationMount = (): Observable<null> => {
    this.actions$.next({
      type: ActionType.Track,
      requestType: RequestType.Parallel,
      data: {
        trackData: {
          actionName:
            TrackActionName.OnCompleteFirstLessonCelebrationMount,
        },
      },
    });

    return of(null);
  };

  public onLandingPageIsMounted = (alias: string): Observable<null> => {
    this.actions$.next({
      type: ActionType.Track,
      requestType: RequestType.Parallel,
      data: {
        trackData: {
          actionName: TrackActionName.OnLandingPageMount,
          alias,
        },
      },
    });

    return of(null);
  };

  public onFreeHeartScreenMount = (): Observable<null> => {
    this.actions$.next({
      type: ActionType.TrackFirstRefillImpression,
      requestType: RequestType.Parallel,
    });

    return of(null);
  };

  public onStreakGoalScreenMount = (): Observable<null> => {
    this.actions$.next({
      type: ActionType.Track,
      requestType: RequestType.Parallel,
      data: {
        trackData: {
          actionName: TrackActionName.OnStreakGoalScreenMount,
        },
      },
    });
    return of(null);
  };

  public onStreakGoalScreenButtonClick = (
    value: number,
  ): Observable<null> => {
    this.actions$.next({
      type: ActionType.NextLCSIteration,
      requestType: RequestType.Parallel,
    });
    this.actions$.next({
      type: ActionType.SetStreakGoal,
      requestType: RequestType.Parallel,
      data: {
        numberValue: value,
      },
    });
    return of(null);
  };

  public onCheckAnswer = (): Observable<null> => {
    this.lessonService.checkAnswer();
    return of(null);
  };

  public onOpenTranslationModalClick = (): Observable<null> => {
    this.data.isTranslationModalOpened$.next(true);

    this.actions$.next({
      type: ActionType.Track,
      requestType: RequestType.Parallel,
      data: {
        trackData: {
          actionName: TrackActionName.OnTranslateCourseButtonClick,
        },
      },
    });

    return of(null);
  };

  public onAICoursePaywall1Mount = (): void => {
    this.actions$.next({
      type: ActionType.Track,
      requestType: RequestType.Parallel,
      data: {
        trackData: {
          actionName: TrackActionName.OnAICoursePaywall1Mount,
        },
      },
    });
  };

  public onAICoursePaywall2Mount = (): void => {
    this.actions$.next({
      type: ActionType.Track,
      requestType: RequestType.Parallel,
      data: {
        trackData: {
          actionName: TrackActionName.OnAICoursePaywall2Mount,
        },
      },
    });
  };

  public onAICoursePaywall1CtaClick = (): void => {
    this.actions$.next({
      type: ActionType.Track,
      requestType: RequestType.Parallel,
      data: {
        trackData: {
          actionName: TrackActionName.OnAICoursePaywall1CtaClick,
        },
      },
    });
    history.push(`${window.location.pathname}${window.location.search}&s=main`);
  };

  public onAICoursePaywall2Close = (): void => {
    this.actions$.next({
      type: ActionType.Track,
      requestType: RequestType.Parallel,
      data: {
        trackData: {
          actionName: TrackActionName.OnAICoursePaywall2Close,
        },
      },
    });
    this.actions$.next({
      type: ActionType.NextLCSIteration,
      requestType: RequestType.Parallel,
    });
  };

  public onAICoursePaywall2CtaClick = (): void => {
    this.actions$.next({
      type: ActionType.Track,
      requestType: RequestType.Parallel,
      data: {
        trackData: {
          actionName: TrackActionName.OnAICoursePaywall2CtaClick,
        },
      },
    });
    this.commonService.navigateToAICoursePaywall();
  };

  public onLessonCompletePaywallInit = (): void => {
    this.actions$.next({
      type: ActionType.OnLessonCompletePaywallInit,
      requestType: RequestType.Parallel
    });
  };
}
